import React from 'react';
import { ImagePlus, X } from 'lucide-react';
import { MembershipPlan } from '../../../services/MasterAdmin/membershipService';
import { StatusBadge } from './MembershipAlerts';

function formatBytes(bytes?: number) {
  const n = Number(bytes || 0);
  if (!n) return '—';
  return `${Math.round(n / (1024 * 1024))} MB`;
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{value || '—'}</div>
    </div>
  );
}

const MembershipPlanViewModal: React.FC<{
  plan: MembershipPlan;
  onClose: () => void;
  onEdit: (plan: MembershipPlan) => void;
}> = ({ plan, onClose, onEdit }) => {
  const doctors = plan.allowedDoctors || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="flex max-h-[92vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl dark:bg-gray-900">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-6 py-4 dark:border-gray-800">
          <div className="flex items-start gap-4">
            {plan.promoImageUrl ? (
              <img
                src={plan.promoImageUrl}
                alt=""
                className={`rounded-lg object-cover ${
                  plan.promoImageOrientation === 'vertical' ? 'h-20 w-14' : 'h-16 w-24'
                }`}
              />
            ) : (
              <div className="flex h-16 w-24 items-center justify-center rounded-lg bg-gray-100 text-gray-400 dark:bg-gray-800">
                <ImagePlus className="h-5 w-5" />
              </div>
            )}
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{plan.name}</h2>
              <p className="text-sm text-gray-500">
                {plan.type} · {plan.title || plan.duration} · ₹{Number(plan.pricing?.payable ?? plan.price).toLocaleString()}
                {Number(plan.gstPercent || 0) > 0
                  ? ` · GST ${Number(plan.gstPercent)}% ${plan.gstMode === 'inclusive' ? 'incl.' : 'excl.'}`
                  : ''}
              </p>
              <div className="mt-2">
                <StatusBadge status={plan.isActive ? 'active' : 'inactive'} />
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
              onClick={() => onEdit(plan)}
            >
              Edit
            </button>
            <button
              type="button"
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="grid min-h-0 flex-1 gap-5 overflow-y-auto p-6 lg:grid-cols-3">
          <section className="space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Basics</h3>
            <Field label="Duration" value={plan.duration} />
            <Field label="Display order" value={Math.max(1, Number(plan.sortOrder) || 1)} />
            <Field label="Price" value={`₹${Number(plan.price).toLocaleString()}`} />
            <Field
              label="GST"
              value={
                Number(plan.gstPercent || 0) > 0
                  ? `${Number(plan.gstPercent)}% ${plan.gstMode === 'inclusive' ? 'inclusive' : 'exclusive'} · ₹${Number(plan.pricing?.gstAmount || 0).toLocaleString()}`
                  : '0% (hidden)'
              }
            />
            <Field label="Payable" value={`₹${Number(plan.pricing?.payable ?? plan.price).toLocaleString()}`} />
            <Field label="Popular" value={plan.isPopular ? 'Yes' : 'No'} />
            <Field label="Description" value={plan.description || '—'} />
          </section>

          <section className="space-y-3 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Entitlements</h3>
            <Field label="Family seats" value={plan.maxFamilyMembers} />
            <Field label="Credits / month" value={plan.consultationCreditsPerMonth} />
            <Field label="Total consults" value={plan.consultationCreditsTotal} />
            <Field
              label="Carry-forward"
              value={
                plan.consultationCreditCarryForward
                  ? `Yes (max ${plan.maxCarryForwardCredits || 0})`
                  : 'No'
              }
            />
            <Field label="Storage" value={formatBytes(plan.storageQuotaBytes)} />
            <Field label="Transfers" value={plan.maxTransfersPerYear} />
            <Field label="GP fee" value={`₹${Number(plan.gpConsultFee || 0)}`} />
            <p className="text-xs text-gray-500">
              Vedika commission is taken from each doctor&apos;s Vendor Commission setting, not from this plan.
            </p>
            <Field label="Dedicated ambulance" value={plan.ambulancePriority ? plan.dedicatedAmbulanceNumber || 'Yes' : 'No'} />
          </section>

          <section className="space-y-4 rounded-xl border border-gray-200 p-4 dark:border-gray-700">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Assigned doctors</h3>
              <p className="mt-1 text-xs text-gray-500">
                Membership credits apply only to these doctor + speciality pairs. Leave empty for GP / Family Physician fallback.
              </p>
              {doctors.length ? (
                <ul className="mt-3 space-y-2">
                  {doctors.map((row) => (
                    <li
                      key={`${row.vendorId}::${row.specialityId}`}
                      className="rounded-lg border border-sky-100 bg-sky-50/70 px-3 py-2 text-sm dark:border-sky-900/40 dark:bg-sky-900/20"
                    >
                      <div className="font-medium text-gray-900 dark:text-white">{row.doctorName}</div>
                      <div className="text-xs text-gray-500">
                        {row.specialityName}
                        {row.clinicName ? ` · ${row.clinicName}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-gray-500">None — GP / Family Physician fallback.</p>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MembershipPlanViewModal;
